import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef, forwardRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface EvaluationCriteriaData {
  evaluationType: string;
  technicalWeightage: number;
  commercialWeightage: number;
  technicalEvaluationCriteria: Array<{ description: string; weightage: number }>;
  commercialEvaluationCriteria: Array<{ description: string; weightage: number }>;
}

@Component({
  selector: 'app-evaluation-criteria',
  templateUrl: './evaluation-criteria.component.html',
  styleUrls: ['./evaluation-criteria.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EvaluationCriteriaComponent),
      multi: true
    }
  ]
})
export class EvaluationCriteriaComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() initialData?: EvaluationCriteriaData;
  @Input() readonly: boolean = false;
  @Input() isPRSelected: boolean = false;
  @Output() dataChange = new EventEmitter<EvaluationCriteriaData>();

  evaluationForm!: FormGroup;
  activeEvaluationTab: 'Technical' | 'Commercial' = 'Technical';

  private trimmedRequiredValidator = (control: any) => {
    const value = control?.value;
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return { required: true };
    }
    return null;
  }

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    if (this.initialData) {
      this.populateForm(this.initialData);
    }
    
    if (this.readonly) {
      this.evaluationForm.disable();
    } else {
      // Apply initial weightage state (Separate = 100% disabled, Joint = editable)
      this.updateWeightageState();
    }

    // Subscribe to evaluationType changes to handle weightage state
    this.evaluationForm.get('evaluationType')?.valueChanges.subscribe((evaluationType: string) => {
      if (!this.readonly) {
        if (evaluationType === 'Separate') {
          // Separate evaluation: ALWAYS fixed at 100% and disabled
          this.evaluationForm.get('technicalWeightage')?.setValue(100.00, { emitEvent: false });
          this.evaluationForm.get('commercialWeightage')?.setValue(100.00, { emitEvent: false });
          this.evaluationForm.get('technicalWeightage')?.disable({ emitEvent: false });
          this.evaluationForm.get('commercialWeightage')?.disable({ emitEvent: false });
        } else if (evaluationType === 'Joint') {
          // Joint evaluation: Set default 50-50 and enable
          this.evaluationForm.get('technicalWeightage')?.setValue(50.00, { emitEvent: false });
          this.evaluationForm.get('commercialWeightage')?.setValue(50.00, { emitEvent: false });
          this.evaluationForm.get('technicalWeightage')?.enable({ emitEvent: false });
          this.evaluationForm.get('commercialWeightage')?.enable({ emitEvent: false });
        }
      }
    });

    // Subscribe to form changes to emit data
    this.evaluationForm.valueChanges.subscribe(() => {
      if (this.evaluationForm) {
        this.emitData();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Get the current PR selection state
    let prSelected = this.isPRSelected;
    if (changes['isPRSelected']) {
      prSelected = changes['isPRSelected'].currentValue;
    }

    // Handle initialData changes
    if (changes['initialData']) {
      const newData = changes['initialData'].currentValue;
      if (newData) {
        this.populateFormWithPRFlag(newData, prSelected);
      }
    }
    
    // Handle readonly changes
    if (changes['readonly']) {
      if (changes['readonly'].currentValue) {
        this.evaluationForm.disable();
      } else {
        this.evaluationForm.enable();
        this.updateWeightageState();
      }
    }
    
    // Handle isPRSelected changes
    if (changes['isPRSelected']) {
      if (!this.readonly) {
        this.updateWeightageState();
      }
    }
  }

  private updateWeightageState(): void {
    // For Separate evaluation: ALWAYS disable (fixed at 100%)
    // For Joint evaluation: ALWAYS enable (editable)
    if (this.isSeparateEvaluation) {
      // Force 100% values for separate evaluation
      this.evaluationForm.get('technicalWeightage')?.setValue(100.00, { emitEvent: false });
      this.evaluationForm.get('commercialWeightage')?.setValue(100.00, { emitEvent: false });
      this.evaluationForm.get('technicalWeightage')?.disable({ emitEvent: false });
      this.evaluationForm.get('commercialWeightage')?.disable({ emitEvent: false });
    } else {
      this.evaluationForm.get('technicalWeightage')?.enable({ emitEvent: false });
      this.evaluationForm.get('commercialWeightage')?.enable({ emitEvent: false });
    }
  }

  private initializeForm(): void {
    this.evaluationForm = this.fb.group({
      evaluationType: ['Separate'],
      technicalWeightage: [100.00],
      commercialWeightage: [100.00],
      technicalEvaluationCriteria: this.fb.array([]),
      commercialEvaluationCriteria: this.fb.array([])
    });
  }

  private populateForm(data: EvaluationCriteriaData): void {
    this.populateFormWithPRFlag(data, this.isPRSelected);
  }

  private populateFormWithPRFlag(data: EvaluationCriteriaData, prSelected: boolean): void {
    // Clear existing criteria
    while (this.technicalEvaluationCriteriaFormArray.length !== 0) {
      this.technicalEvaluationCriteriaFormArray.removeAt(0);
    }
    while (this.commercialEvaluationCriteriaFormArray.length !== 0) {
      this.commercialEvaluationCriteriaFormArray.removeAt(0);
    }

    // Ensure form is enabled before populating (unless readonly)
    if (!this.readonly) {
      this.evaluationForm.enable({ emitEvent: false });
    }

    const evaluationType = data.evaluationType || 'Separate';

    // Set form values - for Separate evaluation, ALWAYS use 100% regardless of PR values
    if (evaluationType === 'Separate') {
      this.evaluationForm.patchValue({
        evaluationType: evaluationType,
        technicalWeightage: 100.00,
        commercialWeightage: 100.00
      }, { emitEvent: false });
    } else {
      // For Joint evaluation, use the values from data
      this.evaluationForm.patchValue({
        evaluationType: evaluationType,
        technicalWeightage: data.technicalWeightage || 50.00,
        commercialWeightage: data.commercialWeightage || 50.00
      }, { emitEvent: false });
    }

    // Populate technical criteria
    (data.technicalEvaluationCriteria || []).forEach(item => {
      this.addEvaluationCriteria('Technical', item.description, item.weightage);
    });

    // Populate commercial criteria
    (data.commercialEvaluationCriteria || []).forEach(item => {
      this.addEvaluationCriteria('Commercial', item.description, item.weightage);
    });

    // Apply weightage state - for Separate, ALWAYS disable (fixed at 100%)
    if (!this.readonly) {
      if (evaluationType === 'Separate') {
        // Separate evaluation: Both fixed at 100%, ALWAYS disabled
        this.evaluationForm.get('technicalWeightage')?.disable({ emitEvent: false });
        this.evaluationForm.get('commercialWeightage')?.disable({ emitEvent: false });
      } else {
        // Joint evaluation: Editable
        this.evaluationForm.get('technicalWeightage')?.enable({ emitEvent: false });
        this.evaluationForm.get('commercialWeightage')?.enable({ emitEvent: false });
      }
    }
  }

  get technicalEvaluationCriteriaFormArray(): FormArray {
    return this.evaluationForm.get('technicalEvaluationCriteria') as FormArray;
  }

  get commercialEvaluationCriteriaFormArray(): FormArray {
    return this.evaluationForm.get('commercialEvaluationCriteria') as FormArray;
  }

  get currentEvaluationCriteriaFormArray(): FormArray {
    return this.activeEvaluationTab === 'Technical' 
      ? this.technicalEvaluationCriteriaFormArray 
      : this.commercialEvaluationCriteriaFormArray;
  }

  get technicalWeightage(): number {
    const rawValue = this.evaluationForm.getRawValue();
    const value = rawValue.technicalWeightage;
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  get commercialWeightage(): number {
    const rawValue = this.evaluationForm.getRawValue();
    const value = rawValue.commercialWeightage;
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  get totalWeightage(): number {
    return this.technicalWeightage + this.commercialWeightage;
  }

  get isJointEvaluation(): boolean {
    return this.evaluationForm.get('evaluationType')?.value === 'Joint';
  }

  get isSeparateEvaluation(): boolean {
    return this.evaluationForm.get('evaluationType')?.value === 'Separate';
  }

  get isEvaluationTypeDisabled(): boolean {
    // NEVER disable evaluation type when PR is selected
    if (this.isPRSelected) {
      return false;
    }
    return this.readonly;
  }

  switchEvaluationTab(tab: 'Technical' | 'Commercial'): void {
    this.activeEvaluationTab = tab;
  }

  onEvaluationTypeChange(evaluationType: 'Separate' | 'Joint'): void {
    // Manually update the form control value
    this.evaluationForm.get('evaluationType')?.setValue(evaluationType);
    // The valueChanges subscription will handle the rest (weightage updates etc.)
  }

  getTotalCriteriaWeightage(): number {
    const formArray = this.currentEvaluationCriteriaFormArray;
    return formArray.controls.reduce((total, control) => {
      const weightage = control.get('weightage')?.value || 0;
      return total + (parseFloat(weightage) || 0);
    }, 0);
  }

  trackByCriteriaControl(index: number, control: any): any {
    return control;
  }
  addEvaluationCriteria(type?: 'Technical' | 'Commercial', description: string = '', weightage: number = 0): void {
    const formArray = type 
      ? (type === 'Technical' ? this.technicalEvaluationCriteriaFormArray : this.commercialEvaluationCriteriaFormArray)
      : this.currentEvaluationCriteriaFormArray;
    
    const newGroup = this.fb.group({
      description: [description, [Validators.required, this.trimmedRequiredValidator]],
      weightage: [weightage]
    });
    
    formArray.push(newGroup);

    if (this.readonly) {
      newGroup.disable();
    }
  }

  removeEvaluationCriteria(index: number): void {
    const formArray = this.currentEvaluationCriteriaFormArray;
    if (formArray.length > 0) {
      formArray.removeAt(index);
    }
  }

  validateCriteriaWeightages(): void {
    // This method can be called from template for validation display updates
  }

  isTechnicalCriteriaValid(): boolean {
    if (this.isSeparateEvaluation) {
      return this.technicalWeightage === 100 && this.getTotalCriteriaWeightage() === 100;
    } else if (this.isJointEvaluation) {
      return Math.abs(this.getTotalCriteriaWeightage() - 100) < 0.01;
    }
    return false;
  }

  isCommercialCriteriaValid(): boolean {
    if (this.isSeparateEvaluation) {
      return this.commercialWeightage === 100 && this.getTotalCriteriaWeightage() === 100;
    } else if (this.isJointEvaluation) {
      return Math.abs(this.getTotalCriteriaWeightage() - 100) < 0.01;
    }
    return false;
  }

  isCurrentCriteriaValid(): boolean {
    if (this.activeEvaluationTab === 'Technical') {
      return this.isTechnicalCriteriaValid();
    } else {
      return this.isCommercialCriteriaValid();
    }
  }

  isCurrentCriteriaWarning(): boolean {
    const total = this.getTotalCriteriaWeightage();
    if (this.isSeparateEvaluation) {
      if (this.activeEvaluationTab === 'Technical') {
        return this.technicalWeightage === 100 && total < 100;
      }
      return this.commercialWeightage === 100 && total < 100;
    }
    return this.isJointEvaluation && Math.abs(total - 100) > 0.01;
  }

  isCurrentCriteriaError(): boolean {
    const total = this.getTotalCriteriaWeightage();
    if (this.isSeparateEvaluation) {
      if (this.activeEvaluationTab === 'Technical') {
        return (this.technicalWeightage === 100 && total !== 100) || total > 100;
      }
      return (this.commercialWeightage === 100 && total !== 100) || total > 100;
    }
    return false;
  }

  getCurrentCriteriaWeightageError(): string {
    const total = this.getTotalCriteriaWeightage();
    if (this.isSeparateEvaluation) {
      if (this.activeEvaluationTab === 'Technical') {
        if (this.technicalWeightage === 100 && total !== 100) {
          return `Total Technical Criteria Weightage must equal exactly 100%. Current: ${total.toFixed(2)}%`;
        } else if (total > 100) {
          return `Total Technical Criteria Weightage cannot exceed 100%. Current: ${total.toFixed(2)}%`;
        }
      } else {
        if (this.commercialWeightage === 100 && total !== 100) {
          return `Total Commercial Criteria Weightage must equal exactly 100%. Current: ${total.toFixed(2)}%`;
        } else if (total > 100) {
          return `Total Commercial Criteria Weightage cannot exceed 100%. Current: ${total.toFixed(2)}%`;
        }
      }
    } else if (this.isJointEvaluation && Math.abs(total - 100) > 0.01) {
      return `Total ${this.activeEvaluationTab} Criteria Weightage is ${total.toFixed(2)}%. It should normally equal 100%, but you can save and complete it later.`;
    }
    return '';
  }

  private emitData(): void {
    const formValue = this.evaluationForm.getRawValue();
    const data: EvaluationCriteriaData = {
      evaluationType: formValue.evaluationType || 'Separate',
      technicalWeightage: this.technicalWeightage,
      commercialWeightage: this.commercialWeightage,
      technicalEvaluationCriteria: this.technicalEvaluationCriteriaFormArray.controls.map(control => ({
        description: control.get('description')?.value || '',
        weightage: parseFloat(control.get('weightage')?.value) || 0
      })),
      commercialEvaluationCriteria: this.commercialEvaluationCriteriaFormArray.controls.map(control => ({
        description: control.get('description')?.value || '',
        weightage: parseFloat(control.get('weightage')?.value) || 0
      }))
    };
    this.dataChange.emit(data);
    this.onChange(data);
  }

  // ControlValueAccessor implementation
  private onChange = (data: EvaluationCriteriaData) => {};
  private onTouched = () => {};

  writeValue(value: EvaluationCriteriaData): void {
    if (value) {
      this.populateForm(value);
    }
  }

  registerOnChange(fn: (data: EvaluationCriteriaData) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.evaluationForm.disable();
    } else {
      this.evaluationForm.enable();
      this.updateWeightageState();
    }
  }
}
